import { ServiceViewDetails, WasteFlow, WastePackaging, WastePhysicalProperties } from '@lib/api-interface';
import { GeneralFormItem, GeneralFormRowOfItems } from '@lib/ui-components';
import { TFunction } from 'i18next';

interface WasteDetailFieldsOptions {
  isVisible?: (data: Record<string, unknown>) => boolean;
}

type FormField = GeneralFormItem | GeneralFormRowOfItems;

const wrapVisibility = (
  baseVisible?: (data: Record<string, unknown>) => boolean,
  fieldVisible?: (data: Record<string, unknown>) => boolean,
): ((data: Record<string, unknown>) => boolean) | undefined => {
  if (baseVisible && fieldVisible) {
    return (data: Record<string, unknown>) => baseVisible(data) && fieldVisible(data);
  }
  return baseVisible || fieldVisible;
};

export const getWastePhysicalPropertyFields = (row: ServiceViewDetails | undefined, t: TFunction, options?: WasteDetailFieldsOptions): FormField[] => {
  const baseVisible = options?.isVisible;

  return [
    {
      rowItems: [
        {
          label: t('wastePhysicalPropertyType'),
          type: 'select',
          field: 'wastePhysicalProperties.propertyType',
          defaultValue: row?.wastePhysicalProperties?.propertyType ?? 'NONE',
          selectOptions: [
            { label: '-', value: 'NONE' },
            { label: t('wastePhysicalPropertyType.DUST'), value: 'DUST' },
            { label: t('wastePhysicalPropertyType.SOLID'), value: 'SOLID' },
            { label: t('wastePhysicalPropertyType.PASTY'), value: 'PASTY' },
            { label: t('wastePhysicalPropertyType.SLUDGE'), value: 'SLUDGE' },
            { label: t('wastePhysicalPropertyType.LIQUID'), value: 'LIQUID' },
            { label: t('wastePhysicalPropertyType.GASEOUS'), value: 'GASEOUS' },
            { label: t('wastePhysicalPropertyType.OTHER'), value: 'OTHER' },
          ],
          isVisible: wrapVisibility(baseVisible),
        },
      ],
    },
    {
      rowItems: [
        {
          label: t('otherPhysicalProperty'),
          type: 'text',
          field: 'wastePhysicalProperties.otherPhysicalProperty',
          defaultValue: row?.wastePhysicalProperties?.otherPhysicalProperty,
          isVisible: wrapVisibility(
            baseVisible,
            (data: Record<string, unknown>) => (data.wastePhysicalProperties as WastePhysicalProperties | undefined)?.propertyType === 'OTHER',
          ),
        },
        {
          label: t('customWasteDescription'),
          type: 'text',
          field: 'wastePhysicalProperties.customWasteDescription',
          defaultValue: row?.wastePhysicalProperties?.customWasteDescription,
          isVisible: wrapVisibility(
            baseVisible,
            (data: Record<string, unknown>) => (data.wastePhysicalProperties as WastePhysicalProperties | undefined)?.propertyType === 'OTHER',
          ),
        },
      ],
    },
  ];
};

export const getWastePackagingFields = (row: ServiceViewDetails | undefined, t: TFunction, options?: WasteDetailFieldsOptions): FormField[] => {
  const baseVisible = options?.isVisible;

  return [
    {
      rowItems: [
        {
          label: t('wastePackagingType'),
          type: 'select',
          field: 'wastePackaging.packagingType',
          defaultValue: row?.wastePackaging?.packagingType ?? 'NONE',
          selectOptions: [
            { label: '-', value: 'NONE' },
            { label: t('wastePackagingType.BULK'), value: 'BULK' },
            { label: t('wastePackagingType.VESSEL'), value: 'VESSEL' },
            { label: t('wastePackagingType.BUCKET'), value: 'BUCKET' },
            { label: t('wastePackagingType.CANISTER'), value: 'CANISTER' },
            { label: t('wastePackagingType.CONTAINER'), value: 'CONTAINER' },
            { label: t('wastePackagingType.BARREL'), value: 'BARREL' },
            { label: t('wastePackagingType.BOX'), value: 'BOX' },
            { label: t('wastePackagingType.BAG'), value: 'BAG' },
            { label: t('wastePackagingType.OTHER'), value: 'OTHER' },
          ],
          isVisible: wrapVisibility(baseVisible),
        },
        {
          label: t('packageNumber'),
          type: 'text',
          field: 'wastePackaging.packageNumber',
          defaultValue: row?.wastePackaging?.packageNumber,
          isVisible: wrapVisibility(baseVisible),
        },
      ],
    },
    {
      rowItems: [
        {
          label: t('otherWastePackaging'),
          type: 'text',
          field: 'wastePackaging.otherWastePackaging',
          defaultValue: row?.wastePackaging?.otherWastePackaging,
          isVisible: wrapVisibility(baseVisible, (data: Record<string, unknown>) => (data.wastePackaging as WastePackaging | undefined)?.packagingType === 'OTHER'),
        },
      ],
    },
  ];
};

export const getWasteFlowFields = (row: ServiceViewDetails | undefined, t: TFunction, options?: WasteDetailFieldsOptions): FormField[] => {
  const baseVisible = options?.isVisible;

  return [
    {
      rowItems: [
        {
          label: t('reportOnWasteProcessing'),
          type: 'boolean',
          field: 'wasteFlow.reportOnWasteProcessing',
          defaultValue: row?.wasteFlow?.reportOnWasteProcessing,
          isVisible: wrapVisibility(baseVisible),
        },
        {
          label: t('wasteFlowPurpose'),
          type: 'select',
          field: 'wasteFlow.purpose',
          defaultValue: row?.wasteFlow?.purpose ?? 'NONE',
          selectOptions: [
            { label: '-', value: 'NONE' },
            { label: t('wasteFlowPurpose.USE'), value: 'USE' },
            { label: t('wasteFlowPurpose.DISPOSAL'), value: 'DISPOSAL' },
          ],
          isVisible: wrapVisibility(baseVisible),
        },
        {
          label: t('wasteFlowQuantityMethod'),
          type: 'select',
          field: 'wasteFlow.quantityMethod',
          defaultValue: row?.wasteFlow?.quantityMethod ?? 'NONE',
          selectOptions: [
            { label: '-', value: 'NONE' },
            { label: t('wasteFlowQuantityMethod.QUANTITY_WEIGHING'), value: 'QUANTITY_WEIGHING' },
            { label: t('wasteFlowQuantityMethod.QUANTITY_ESTIMATE'), value: 'QUANTITY_ESTIMATE' },
          ],
          isVisible: wrapVisibility(baseVisible),
        },
        {
          label: t('wasteFlowTransportMethod'),
          type: 'select',
          field: 'wasteFlow.transportMethod',
          defaultValue: row?.wasteFlow?.transportMethod ?? 'NONE',
          selectOptions: [
            { label: '-', value: 'NONE' },
            { label: t('wasteFlowTransportMethod.ROAD'), value: 'ROAD' },
            { label: t('wasteFlowTransportMethod.RAIL'), value: 'RAIL' },
            { label: t('wasteFlowTransportMethod.SEA'), value: 'SEA' },
            { label: t('wasteFlowTransportMethod.AIR'), value: 'AIR' },
            { label: t('wasteFlowTransportMethod.INLAND_WATERWAYS'), value: 'INLAND_WATERWAYS' },
          ],
          isVisible: wrapVisibility(baseVisible),
        },
      ],
    },
    {
      rowItems: [
        {
          label: t('senderDateOfDelivery'),
          type: 'date',
          field: 'wasteFlow.senderDateOfDelivery',
          defaultValue: row?.wasteFlow?.senderDateOfDelivery,
          isVisible: wrapVisibility(baseVisible),
        },
        {
          label: t('wasteFlowLicensePlates'),
          type: 'text',
          field: 'wasteFlow.wasteFlowLicensePlates',
          defaultValue: row?.wasteFlow?.wasteFlowLicensePlates,
          isVisible: wrapVisibility(baseVisible),
        },
        {
          label: t('carrierReceivedBy'),
          type: 'text',
          field: 'wasteFlow.carrierReceivedBy',
          defaultValue: row?.wasteFlow?.carrierReceivedBy,
          isVisible: wrapVisibility(baseVisible),
        },
      ],
    },
    {
      rowItems: [
        {
          label: t('carrierDeliveryDate'),
          type: 'date',
          field: 'wasteFlow.carrierDeliveryDate',
          defaultValue: row?.wasteFlow?.carrierDeliveryDate,
          isVisible: wrapVisibility(baseVisible),
        },
        {
          label: t('carrierSubmittedBy'),
          type: 'text',
          field: 'wasteFlow.carrierSubmittedBy',
          defaultValue: row?.wasteFlow?.carrierSubmittedBy,
          isVisible: wrapVisibility(baseVisible),
        },
        {
          label: t('recipientReceivedBy'),
          type: 'text',
          field: 'wasteFlow.recipientReceivedBy',
          defaultValue: row?.wasteFlow?.recipientReceivedBy,
          isVisible: wrapVisibility(baseVisible),
        },
      ],
    },
    {
      rowItems: [
        {
          label: t('recipientDateOfWeighing'),
          type: 'date',
          field: 'wasteFlow.recipientDateOfWeighing',
          defaultValue: row?.wasteFlow?.recipientDateOfWeighing,
          isVisible: wrapVisibility(baseVisible),
        },
        {
          label: t('recipientQuantityTakenInKg'),
          type: 'text',
          field: 'wasteFlow.recipientQuantityTakenInKg',
          defaultValue: row?.wasteFlow?.recipientQuantityTakenInKg,
          isVisible: wrapVisibility(baseVisible),
        },
      ],
    },
  ];
};

export const getWasteDetailFields = (row: ServiceViewDetails | undefined, t: TFunction, options?: WasteDetailFieldsOptions): FormField[] => {
  return [...getWastePhysicalPropertyFields(row, t, options), ...getWastePackagingFields(row, t, options), ...getWasteFlowFields(row, t, options)];
};
